import { useState } from 'react';
import {
  MEMBER_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  canInviteRole,
  type MemberRole,
  type Permission,
} from '@mustauto/types/permissions';
import { Card, CardContent, cn } from '@mustauto/ui/web';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';

/** A ordem do convite: Mecânico primeiro (é o convite de sempre), Gestor por último. */
const INVITE_ORDER: MemberRole[] = ['mechanic', 'reception', 'finance', 'manager'];

/**
 * Convite com papel: a escolha vem antes de gerar o código, com Mecânico
 * marcado — quem só gera o convite, como sempre fez, convida do mesmo jeito.
 * O papel que quem convida não pode dar (não tem tudo o que ele recebe) fica
 * desabilitado, com o motivo; a API recusa do mesmo jeito.
 */
interface InviteDialogProps {
  viewerPermissions: readonly Permission[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: (role: MemberRole) => void;
}

export function InviteDialog({ open, ...props }: InviteDialogProps & { open: boolean }) {
  // Montado só aberto: o foco preso e o Esc valem enquanto o diálogo existe.
  return open ? <InviteDialogBody {...props} /> : null;
}

function InviteDialogBody({ viewerPermissions, pending, onCancel, onConfirm }: InviteDialogProps) {
  // Mecânico marcado — ou o primeiro papel que quem convida pode dar.
  const [role, setRole] = useState<MemberRole | null>(() => {
    const perms = new Set(viewerPermissions);
    return INVITE_ORDER.find((r) => canInviteRole(perms, r)) ?? null;
  });
  const trapRef = useFocusTrap<HTMLDivElement>();
  useEscapeKey(onCancel);

  const perms = new Set(viewerPermissions);
  const roles = INVITE_ORDER.filter((r) => (MEMBER_ROLES as readonly string[]).includes(r));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby="invite-title" onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
        <Card>
          <CardContent className="space-y-4 p-5">
            <div>
              <h2 id="invite-title" className="text-lg font-semibold">Convidar para a equipe</h2>
              <p className="text-sm text-muted-foreground">
                A pessoa entra com este papel. Você pode ajustar o que ela faz depois, na ficha dela.
              </p>
            </div>
            <div className="space-y-2" role="radiogroup" aria-label="Papel">
              {roles.map((r) => {
                const allowed = canInviteRole(perms, r);
                return (
                  <label
                    key={r}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors',
                      role === r && 'border-primary bg-accent',
                      !allowed && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <input
                      type="radio"
                      name="invite-role"
                      value={r}
                      checked={role === r}
                      disabled={!allowed}
                      onChange={() => setRole(r)}
                      className="mt-1 accent-primary"
                    />
                    <span>
                      <span className="block text-sm font-medium">{ROLE_LABELS[r]}</span>
                      <span className="block text-xs text-muted-foreground">
                        {allowed ? ROLE_DESCRIPTIONS[r] : 'Você não pode liberar tudo o que este papel tem.'}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onCancel} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted">
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => role && onConfirm(role)}
                disabled={pending || role === null}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {pending ? 'Gerando...' : 'Gerar convite'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
